/**
 * The Settings page, route `#/settings[/section]` (W2-13 settings-keymap; DESIGN.md §3.1, §4.20, §5.15).
 *
 * One searchable page: every section (General, Editing, Audio, Preview & skins, Keyboard, Checker, Storage, Panels
 * and tools, About & licences) is stacked in one scrolling column with a section navigation on the left that
 * follows the scroll position. The search filters rows by title, description and keywords, the keymap editor by
 * command, key and context, and the checker rules by name; the navigation shows the number of matches per section.
 * A block found by its own title or keywords ("conflict", "warnings") counts as one match and is shown whole; one
 * found only through its contents shows the matching commands or rules, one match each.
 * The route parameter is a section id, a row id (`audio.latency.calibrationMs`) or a setting path: the page scrolls
 * there and highlights the row (palette `:` results and `settings.open { section }` use it).
 * Every row applies live and has Reset; rows contributed by feature modules (`FeatureModule.settings`, incl.
 * `ext.<feature>.*`) are rendered with the same row component.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { AppServices } from '../../app/registry/types';
import { Button, EmptyState, Icon } from '../../ui';
import { AboutSection } from './AboutSection';
import { CheckerSection, ruleSearchMatches } from './CheckerSection';
import { prefersReducedMotion, useKeymapBindings, useServicesVersion, useSettingsValue, useText } from './hooks';
import { KeymapEditor } from './KeymapEditor';
import { buildKeymapView, filterKeymapView } from './keymapView';
import { searchTerms } from './keymapModel';
import { ROW_HOOKS } from './rowExtras';
import { SearchField } from './SearchField';
import { SettingRow } from './SettingRow';
import {
  PAGE_SECTIONS,
  builtinRows,
  itemMatches,
  pageItems,
  sectionOfAnchor,
  type BlockId,
  type BlockItem,
  type PageItem,
  type PageSectionId,
  type RowItem,
} from './settingsCatalog';
import { m } from './settings.messages';
import { SettingsNav } from './SettingsNav';
import { SkinsSection } from './SkinsSection';
import { StorageSection } from './StorageSection';
import s from './Settings.module.css';

export interface SettingsRouteProps {
  readonly services: AppServices;
  readonly params: Readonly<Record<string, string>>;
}

/** How long a deep-linked row stays highlighted (ms). */
const HIGHLIGHT_MS = 2400;

const BUILTIN_ROWS = builtinRows(ROW_HOOKS);

/** Page items of the current modules (built-in rows, module rows, blocks). */
export function settingsPageItems(services: Pick<AppServices, 'registry'>): PageItem[] {
  return pageItems(BUILTIN_ROWS, services.registry.modules);
}

/** Gap kept above a section scrolled to the top (px). */
const SCROLL_GAP = 12;

/**
 * Scrolls the page's own content column (never the document or the shell around it) so that `el` is at the top
 * or in the middle.
 */
function scrollTo(
  container: HTMLElement | null,
  el: Element | null,
  opts: { readonly smooth: boolean; readonly block: 'start' | 'center' },
): void {
  if (container === null || el === null) return;
  const c = container.getBoundingClientRect();
  const r = el.getBoundingClientRect();
  const offset = r.top - c.top;
  const top =
    opts.block === 'start'
      ? container.scrollTop + offset - SCROLL_GAP
      : container.scrollTop + offset - Math.max(0, (c.height - r.height) / 2);
  const target = Math.max(0, top);
  if (typeof container.scrollTo === 'function')
    container.scrollTo({ top: target, behavior: opts.smooth ? 'smooth' : 'auto' });
  else container.scrollTop = target;
}

export function SettingsRoute(props: SettingsRouteProps) {
  const { services } = props;
  const text = useText(services);
  const { t, tr } = text;
  const settings = useSettingsValue(services);
  const effective = useKeymapBindings(services);
  useServicesVersion(services);
  const guided = settings.general.level === 'guided';
  const reducedMotion = prefersReducedMotion(settings.general.reducedMotion);

  const [query, setQueryState] = useState('');
  /** The search text for effects that must not re-run on every key stroke. */
  const queryRef = useRef('');
  const setQuery = useCallback((q: string): void => {
    queryRef.current = q;
    setQueryState(q);
  }, []);
  const terms = useMemo(() => searchTerms(query), [query]);
  const searching = terms.length > 0;

  const items = useMemo(() => settingsPageItems(services), [services]);
  const hasFeatures = items.some((i) => i.section === 'features');
  const sections = useMemo(() => PAGE_SECTIONS.filter((sec) => sec.id !== 'features' || hasFeatures), [hasFeatures]);

  // Search: rows and blocks by their texts; the keymap block also by its commands, the rules block by its rules.
  // The keymap rows are built once per keymap change while searching (not per key stroke); the search filters them.
  const keymapOverrides = settings.keyboard.overrides;
  const keymapView = useMemo(
    () => (searching ? buildKeymapView(services, t, tr, { effective, overrides: keymapOverrides }) : null),
    [searching, effective, keymapOverrides, services, t, tr],
  );
  const keymapHits = useMemo(
    () => (keymapView === null ? 0 : filterKeymapView(keymapView, { query, filter: 'all' }).length),
    [keymapView, query],
  );
  const ruleHits = useMemo(
    () => (searching ? ruleSearchMatches(services, tr, query) : 0),
    [searching, services, tr, query],
  );
  /** Blocks found by their own title or keywords: shown whole (the search does not filter their contents). */
  const ownMatches = useMemo(() => {
    const out = new Set<BlockId>();
    if (!searching) return out;
    for (const item of items) if (item.kind === 'block' && itemMatches(t, item, terms)) out.add(item.id);
    return out;
  }, [items, searching, t, terms]);
  const visible = useMemo(() => {
    if (!searching) return items;
    return items.filter((item) => {
      if (itemMatches(t, item, terms)) return true;
      if (item.kind === 'block' && item.id === 'keymap') return keymapHits > 0;
      if (item.kind === 'block' && item.id === 'checkerRules') return ruleHits > 0;
      return false;
    });
  }, [items, searching, t, terms, keymapHits, ruleHits]);
  const counts = useMemo(() => {
    if (!searching) return null;
    const map = new Map<PageSectionId, number>();
    for (const item of visible) {
      let n = 1;
      if (item.kind === 'block' && !ownMatches.has(item.id)) {
        if (item.id === 'keymap') n = keymapHits;
        else if (item.id === 'checkerRules') n = ruleHits;
      }
      map.set(item.section, (map.get(item.section) ?? 0) + n);
    }
    return map;
  }, [searching, visible, ownMatches, keymapHits, ruleHits]);

  // ---- scrolling, deep links, scroll-spy ----
  const contentRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const [current, setCurrent] = useState<PageSectionId>('general');
  const [highlighted, setHighlighted] = useState<string | null>(null);
  /** Section the user picked in the navigation (its hash change must not scroll a second time). */
  const navigated = useRef<string | null>(null);
  const anchor = props.params.section ?? null;

  useEffect(() => {
    if (anchor === null || anchor === '') {
      searchRef.current?.focus({ preventScroll: true });
      return undefined;
    }
    if (navigated.current === anchor) {
      navigated.current = null;
      return undefined;
    }
    const section = sectionOfAnchor(items, anchor);
    if (section === null) return undefined;
    const item = items.find((i) => i.id === anchor || (i.kind === 'row' && i.path === anchor));
    const frame = requestAnimationFrame(() => {
      setCurrent(section);
      // A deep link to a row the search hides clears the search, so the row can be shown.
      if (item !== undefined && item.id !== section && queryRef.current !== '') setQuery('');
      requestAnimationFrame(() => {
        const root = contentRef.current;
        if (root === null) return;
        if (item === undefined || item.id === section) {
          scrollTo(root, root.querySelector(`#settings-section-${section}`), { smooth: false, block: 'start' });
          return;
        }
        const el =
          [...root.querySelectorAll<HTMLElement>('[data-setting]')].find((x) => x.dataset.setting === item.id) ?? null;
        scrollTo(root, el, { smooth: false, block: 'center' });
        el?.focus({ preventScroll: true });
        setHighlighted(item.id);
      });
    });
    return () => {
      cancelAnimationFrame(frame);
    };
  }, [anchor, items, setQuery]);

  useEffect(() => {
    if (highlighted === null) return undefined;
    const timer = setTimeout(() => {
      setHighlighted(null);
    }, HIGHLIGHT_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [highlighted]);

  const onScroll = (): void => {
    const root = contentRef.current;
    if (root === null) return;
    const top = root.getBoundingClientRect().top;
    let best: PageSectionId | null = null;
    for (const el of root.querySelectorAll<HTMLElement>('[data-section]')) {
      if (el.getBoundingClientRect().top - top <= 48) best = el.dataset.section as PageSectionId;
    }
    if (best !== null && best !== current) setCurrent(best);
  };

  const selectSection = (id: PageSectionId): void => {
    setCurrent(id);
    const root = contentRef.current;
    scrollTo(root, root?.querySelector(`#settings-section-${id}`) ?? null, { smooth: !reducedMotion, block: 'start' });
    // The address follows the section (reload and Back keep it); while searching only the view scrolls, so the
    // search results stay as they are.
    if (searching) return;
    navigated.current = id;
    services.nav.goSettings(id);
  };

  // ---- header actions ----
  const projectId = services.editor.getState().projectId;
  const back = (): void => {
    if (projectId !== null) void services.nav.openProject(projectId, services.editor.getState().workspace);
    else services.nav.goLibrary();
  };
  const resetAll = async (): Promise<void> => {
    const ok = await services.ui.confirm({
      title: { key: m.resetAllTitle },
      body: { key: m.resetAllBody },
      confirm: { key: m.resetAllConfirm },
      danger: true,
    });
    if (!ok) return;
    services.settings.reset();
    const locale = services.settings.value.general.locale;
    if (services.i18n.locale !== locale) services.i18n.setLocale(locale);
    services.ui.toast({ key: m.resetAllDone }, { kind: 'success' });
  };

  // ---- render ----
  const visibleSections = sections.filter((sec) => visible.some((i) => i.section === sec.id));
  // While searching, the section in view is one with results.
  const navCurrent =
    searching && !visibleSections.some((sec) => sec.id === current) ? (visibleSections[0]?.id ?? current) : current;

  return (
    <div className={s.page} data-region="settings">
      <header className={s.header}>
        <div className={s.headerTitle}>
          <Icon name="Settings" size={20} />
          <h1 className={s.h1}>{t(m.title)}</h1>
        </div>
        <SearchField
          ref={searchRef}
          className={s.pageSearch}
          value={query}
          onChange={setQuery}
          label={t(m.searchLabel)}
          placeholder={t(m.searchPlaceholder)}
        />
        <div className={s.headerActions}>
          <Button
            variant="ghost"
            icon="RotateCcw"
            onClick={() => {
              void resetAll();
            }}
          >
            {t(m.resetAll)}
          </Button>
          <Button variant="secondary" icon="ArrowLeft" onClick={back}>
            {t(projectId !== null ? m.backToChart : m.backToLibrary)}
          </Button>
        </div>
      </header>
      <div className={s.body}>
        <SettingsNav sections={sections} current={navCurrent} counts={counts} text={text} onSelect={selectSection} />
        <div ref={contentRef} className={s.content} onScroll={onScroll}>
          {searching && (
            <p className={s.searchStatus} role="status">
              {visibleSections.length === 0
                ? t(m.searchNone, { query })
                : t(m.searchResults, { count: [...(counts?.values() ?? [])].reduce((a, b) => a + b, 0) })}
            </p>
          )}
          {searching && visibleSections.length === 0 && (
            <EmptyState
              icon="SearchX"
              title={{ key: m.searchNone, params: { query } }}
              body={{ key: m.searchNoneHint }}
            />
          )}
          {visibleSections.map((sec) => (
            <section
              key={sec.id}
              id={`settings-section-${sec.id}`}
              data-section={sec.id}
              className={s.section}
              aria-labelledby={`settings-heading-${sec.id}`}
            >
              <div className={s.sectionHeader}>
                <Icon name={sec.icon} size={18} />
                <div>
                  <h2 id={`settings-heading-${sec.id}`} className={s.sectionTitle}>
                    {t(sec.title)}
                  </h2>
                  <p className={s.sectionDesc}>{t(sec.description)}</p>
                </div>
              </div>
              <SectionItems
                services={services}
                items={visible.filter((i) => i.section === sec.id)}
                settings={settings}
                text={text}
                guided={guided}
                highlighted={highlighted}
                query={query}
                ownMatches={ownMatches}
              />
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}

interface SectionItemsProps {
  readonly services: AppServices;
  readonly items: readonly PageItem[];
  readonly settings: ReturnType<typeof useSettingsValue>;
  readonly text: ReturnType<typeof useText>;
  readonly guided: boolean;
  readonly highlighted: string | null;
  readonly query: string;
  /** Blocks the search found by their own texts (their contents are not filtered). */
  readonly ownMatches: ReadonlySet<BlockId>;
}

/** Rows (with their group sub-headings) and blocks of one section. */
function SectionItems(props: SectionItemsProps) {
  const { t } = props.text;
  const out: ReactNode[] = [];
  let group: string | undefined;
  for (const item of props.items) {
    if (item.kind === 'row') {
      if (item.group !== undefined && item.group !== group) {
        out.push(
          <h3 key={`group:${item.group}`} className={s.groupTitle}>
            {t(item.group)}
          </h3>,
        );
      }
      group = item.group;
      out.push(
        <SettingRow
          key={item.id}
          services={props.services}
          item={item}
          settings={props.settings}
          text={props.text}
          guided={props.guided}
          highlighted={props.highlighted === item.id}
        />,
      );
    } else {
      group = undefined;
      out.push(<Block key={item.id} {...props} block={item} />);
    }
  }
  return <div className={s.items}>{out}</div>;
}

function Block(props: SectionItemsProps & { readonly block: BlockItem }) {
  const { block, services, text } = props;
  // A block the search found by its own title or keywords lists everything; otherwise only what matches.
  const query = props.ownMatches.has(block.id) ? '' : props.query;
  let body: ReactNode;
  switch (block.id) {
    case 'keymap':
      body = <KeymapEditor services={services} text={text} query={query} />;
      break;
    case 'skins':
      body = <SkinsSection services={services} text={text} />;
      break;
    case 'checkerRules':
      body = <CheckerSection services={services} text={text} query={query} />;
      break;
    case 'storage':
      body = <StorageSection services={services} text={text} />;
      break;
    case 'about':
      body = <AboutSection services={services} text={text} />;
      break;
  }
  return (
    <div
      className={s.block}
      data-setting={block.id}
      data-highlighted={props.highlighted === block.id ? 'true' : undefined}
      tabIndex={-1}
    >
      {body}
    </div>
  );
}

/** Rows of the page (built-in and module rows) for the `:` palette provider. */
export function settingsRows(services: Pick<AppServices, 'registry'>): RowItem[] {
  return settingsPageItems(services).filter((i): i is RowItem => i.kind === 'row');
}
